import React, { useMemo, useState } from "react";
import { FileText, Instagram, Mail, Search, ImageIcon, Film, Calculator } from "lucide-react";

/**
 * CreditsCalculator — turns "120 credits" from a technical unit into an
 * intuitive answer to "what could I actually make?". Interactive sliders let
 * a visitor mix their own week and see they get a lot for zero dollars.
 */

const OUTPUTS = [
  { id: "blog",   label: "Blog posts",     cost: 1,  icon: FileText,  color: "text-orange-500" },
  { id: "social", label: "Social posts",   cost: 1,  icon: Instagram, color: "text-pink-500" },
  { id: "email",  label: "Emails",         cost: 1,  icon: Mail,      color: "text-blue-500" },
  { id: "seo",    label: "SEO packs",      cost: 1,  icon: Search,    color: "text-emerald-500" },
  { id: "image",  label: "Editorial images", cost: 5, icon: ImageIcon, color: "text-purple-500" },
  { id: "video",  label: "Cinematic clips", cost: 40, icon: Film,     color: "text-rose-500" },
];

const PRESETS = [
  { id: "content-week", label: "Content week", mix: { blog: 1, social: 10, email: 2, seo: 1, image: 4, video: 1 } },
  { id: "brand-launch", label: "Brand launch", mix: { blog: 3, social: 15, email: 4, seo: 3, image: 8, video: 1 } },
  { id: "social-focus", label: "Social focus", mix: { blog: 0, social: 30, email: 0, seo: 0, image: 12, video: 0 } },
  { id: "founder-blog", label: "Founder blog", mix: { blog: 8, social: 24, email: 4, seo: 8, image: 6, video: 1 } },
];

const TOTAL_CREDITS = 120;

export default function CreditsCalculator() {
  const [mix, setMix] = useState(PRESETS[0].mix);

  const spent = useMemo(() => Object.entries(mix).reduce((acc, [k, v]) => {
    const cfg = OUTPUTS.find((o) => o.id === k);
    return acc + (cfg ? cfg.cost * v : 0);
  }, 0), [mix]);

  const remaining = TOTAL_CREDITS - spent;
  const over = remaining < 0;

  function set(id, val) {
    setMix((m) => ({ ...m, [id]: Math.max(0, val) }));
  }

  function loadPreset(p) {
    setMix(p.mix);
  }

  return (
    <section className="border-b border-border" data-testid="credits-calculator-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="grid md:grid-cols-[1fr_1.4fr] gap-10 md:gap-16 items-start">
          <div>
            <div className="overline text-primary mb-4 flex items-center gap-2">
              <Calculator className="w-4 h-4" /> Your first 120 credits
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight tracking-tight">
              What could you<br />
              <span className="italic text-primary">actually make?</span>
            </h2>
            <p className="mt-5 text-sm md:text-base text-muted-foreground max-w-md">
              120 credits is enough fuel to explore the whole Draftwave workflow — plus a full week
              of content, or a launch campaign, or your first cinematic short. Mix and match.
              Credits never expire.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  onClick={() => loadPreset(p)}
                  className="px-3 py-1.5 border border-border overline text-[10px] hover:bg-foreground hover:text-background transition"
                  data-testid={`calc-preset-${p.id}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="border border-border p-5 md:p-8 bg-background/80 backdrop-blur">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
              {OUTPUTS.map((o) => {
                const Icon = o.icon;
                const val = mix[o.id] || 0;
                return (
                  <div key={o.id} className="flex flex-col gap-2" data-testid={`calc-${o.id}`}>
                    <div className="flex items-center gap-2">
                      <Icon className={`w-4 h-4 ${o.color}`} />
                      <span className="overline text-[10px]">{o.label}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="font-serif-display text-4xl leading-none tabular-nums">{val}</span>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                        × {o.cost}c
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => set(o.id, val - 1)}
                        className="w-7 h-7 border border-border overline text-xs hover:bg-foreground hover:text-background transition"
                        data-testid={`calc-${o.id}-dec`}
                      >
                        −
                      </button>
                      <button
                        type="button"
                        onClick={() => set(o.id, val + 1)}
                        className="w-7 h-7 border border-border overline text-xs hover:bg-foreground hover:text-background transition"
                        data-testid={`calc-${o.id}-inc`}
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 pt-6 border-t border-border grid grid-cols-2 gap-4">
              <div>
                <div className="overline text-[10px] text-muted-foreground mb-1">Credits used</div>
                <div className={"font-serif-display text-3xl md:text-4xl tabular-nums " + (over ? "text-red-500" : "text-foreground")}>
                  {spent}<span className="text-lg text-muted-foreground">/{TOTAL_CREDITS}</span>
                </div>
              </div>
              <div>
                <div className="overline text-[10px] text-muted-foreground mb-1">Remaining</div>
                <div className={"font-serif-display text-3xl md:text-4xl tabular-nums " + (over ? "text-red-500" : "text-primary")}>
                  {over ? "−" : ""}{Math.abs(remaining)}
                </div>
              </div>
            </div>
            <div className="mt-4 h-1.5 bg-muted overflow-hidden">
              <div
                className={"h-full transition-all duration-300 " + (over ? "bg-red-500" : "bg-primary")}
                style={{ width: `${Math.min(100, Math.round((spent / TOTAL_CREDITS) * 100))}%` }}
              />
            </div>
            <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              {over ? `Over by ${Math.abs(remaining)}c — upgrade to Starter for 400/mo` : `${remaining}c of runway left to explore`}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
